import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, XCircle, Clock, BookOpen, Map, ListChecks, ShieldCheck, ChevronDown, Leaf, XIcon } from 'lucide-react';
import { initPixel, trackPageView, trackAddToCart } from './utils/pixel';
const FadeIn = ({
  children,
  delay = 0
}: {
  children: React.ReactNode;
  delay?: number;
}) => <motion.div initial={{
  opacity: 0,
  y: 20
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-100px'
}} transition={{
  duration: 0.6,
  delay
}} data-id="element-0">
    {children}
  </motion.div>;
const LeafDecoration = ({
  className
}: {
  className?: string;
}) => <div className={`text-sage opacity-20 ${className}`} data-id="element-1">
    <Leaf size={48} strokeWidth={1} data-id="element-2" />
  </div>;
const Button = ({
  children,
  onClick,
  className = ''
}: {
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
}) => <button onClick={onClick} className={`bg-sage hover:bg-sage-dark text-white font-bold py-4 px-8 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 flex items-center justify-center gap-2 text-lg w-full md:w-auto ${className}`} data-id="element-3">
    {children}
  </button>;
type LegalModalType = 'privacy' | 'terms' | null;
function LegalModal({
  type,
  onClose
}: {
  type: LegalModalType;
  onClose: () => void;
}) {
  if (!type) return null;
  const content = {
    privacy: {
      title: 'Política de Privacidad',
      body: <div className="space-y-4 text-warmgray text-sm leading-relaxed" data-id="element-4">
          <p data-id="element-5">
            <strong className="text-olive" data-id="element-6">Última actualización:</strong> Abril
            2026
          </p>
          <p data-id="element-7">
            En Método Casa en Calma, respetamos tu privacidad y nos
            comprometemos a proteger tus datos personales. Esta política
            describe cómo recopilamos, usamos y protegemos tu información.
          </p>

          <h4 className="text-olive font-bold text-base mt-6" data-id="element-8">
            1. Información que recopilamos
          </h4>
          <p data-id="element-9">
            Al realizar tu compra, recopilamos únicamente la información
            necesaria para procesar tu pedido: nombre, dirección de correo
            electrónico y datos de pago (procesados de forma segura por nuestro
            proveedor de pagos).
          </p>

          <h4 className="text-olive font-bold text-base mt-6" data-id="element-10">
            2. Uso de la información
          </h4>
          <p data-id="element-11">Utilizamos tu información exclusivamente para:</p>
          <ul className="list-disc pl-5 space-y-1" data-id="element-12">
            <li data-id="element-13">Enviarte el producto digital adquirido.</li>
            <li data-id="element-14">Comunicarnos contigo respecto a tu compra.</li>
            <li data-id="element-15">Enviarte actualizaciones del producto (si las hubiera).</li>
          </ul>
          <p data-id="element-16">
            No vendemos, alquilamos ni compartimos tu información personal con
            terceros con fines comerciales.
          </p>

          <h4 className="text-olive font-bold text-base mt-6" data-id="element-17">
            3. Protección de datos
          </h4>
          <p data-id="element-18">
            Implementamos medidas de seguridad técnicas y organizativas para
            proteger tu información personal contra acceso no autorizado,
            pérdida o alteración.
          </p>

          <h4 className="text-olive font-bold text-base mt-6" data-id="element-19">4. Cookies</h4>
          <p data-id="element-20">
            Este sitio puede utilizar cookies para mejorar tu experiencia de
            navegación y analizar el tráfico del sitio. Podés configurar tu
            navegador para rechazar cookies en cualquier momento.
          </p>

          <h4 className="text-olive font-bold text-base mt-6" data-id="element-21">
            5. Tus derechos
          </h4>
          <p data-id="element-22">
            Tenés derecho a acceder, rectificar o eliminar tus datos personales
            en cualquier momento. Para ejercer estos derechos, podés comunicarte
            con nosotros a través de los canales indicados en tu email de
            compra.
          </p>

          <h4 className="text-olive font-bold text-base mt-6" data-id="element-23">
            6. Cambios en esta política
          </h4>
          <p data-id="element-24">
            Nos reservamos el derecho de actualizar esta política en cualquier
            momento. Los cambios serán publicados en esta misma página.
          </p>
        </div>
    },
    terms: {
      title: 'Términos y Condiciones',
      body: <div className="space-y-4 text-warmgray text-sm leading-relaxed" data-id="element-25">
          <p data-id="element-26">
            <strong className="text-olive" data-id="element-27">Última actualización:</strong> Abril
            2026
          </p>
          <p data-id="element-28">
            Al adquirir el producto digital "Método Casa en Calma", aceptás los
            siguientes términos y condiciones de uso.
          </p>

          <h4 className="text-olive font-bold text-base mt-6" data-id="element-29">
            1. Producto digital
          </h4>
          <p data-id="element-30">
            El Método Casa en Calma es un producto digital (ebook y materiales
            complementarios) que se entrega de forma inmediata por correo
            electrónico tras confirmar el pago. No se realizan envíos físicos.
          </p>

          <h4 className="text-olive font-bold text-base mt-6" data-id="element-31">
            2. Licencia de uso
          </h4>
          <p data-id="element-32">
            Al comprar el producto, obtenés una licencia personal e
            intransferible para uso individual. Queda prohibida la reproducción,
            distribución, reventa o compartición del material con terceros.
          </p>

          <h4 className="text-olive font-bold text-base mt-6" data-id="element-33">
            3. Política de reembolso
          </h4>
          <p data-id="element-34">
            Debido a la naturaleza digital del producto y su entrega inmediata,
            no se aceptan devoluciones ni reembolsos una vez realizada la compra
            y enviado el acceso al material.
          </p>

          <h4 className="text-olive font-bold text-base mt-6" data-id="element-35">
            4. Propiedad intelectual
          </h4>
          <p data-id="element-36">
            Todo el contenido del Método Casa en Calma (textos, diseños,
            planillas, metodología) está protegido por derechos de autor. Queda
            prohibida su reproducción total o parcial sin autorización expresa.
          </p>

          <h4 className="text-olive font-bold text-base mt-6" data-id="element-37">5. Resultados</h4>
          <p data-id="element-38">
            Los resultados pueden variar según la dedicación y constancia de
            cada persona. No garantizamos resultados específicos, ya que
            dependen del compromiso individual con el método.
          </p>

          <h4 className="text-olive font-bold text-base mt-6" data-id="element-39">
            6. Medios de pago
          </h4>
          <p data-id="element-40">
            Aceptamos pagos a través de Mercado Pago, tarjetas de crédito y
            tarjetas de débito. Todos los pagos son procesados de forma segura
            por proveedores certificados.
          </p>

          <h4 className="text-olive font-bold text-base mt-6" data-id="element-41">
            7. Modificaciones
          </h4>
          <p data-id="element-42">
            Nos reservamos el derecho de modificar estos términos en cualquier
            momento. Las modificaciones entrarán en vigencia desde su
            publicación en este sitio.
          </p>
        </div>
    }
  };
  const {
    title,
    body
  } = content[type];
  return <AnimatePresence data-id="element-43">
      <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} data-id="element-44">
        <div className="absolute inset-0 bg-olive/60 backdrop-blur-sm" onClick={onClose} data-id="element-45" />
        <motion.div className="relative bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[85vh] flex flex-col overflow-hidden" initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.3
      }} data-id="element-46">
          <div className="flex items-center justify-between p-6 border-b border-cream" data-id="element-47">
            <h3 className="text-2xl font-serif text-olive" data-id="element-48">{title}</h3>
            <button onClick={onClose} className="p-2 rounded-full hover:bg-cream transition-colors" aria-label="Cerrar" data-id="element-49">
              <XIcon size={20} className="text-warmgray" data-id="element-50" />
            </button>
          </div>
          <div className="p-6 md:p-8 overflow-y-auto" data-id="element-51">{body}</div>
          <div className="p-4 border-t border-cream text-center" data-id="element-52">
            <button onClick={onClose} className="text-sm font-bold text-sage hover:text-sage-dark transition-colors" data-id="element-53">
              Cerrar
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>;
}
export function App() {
  const [legalModal, setLegalModal] = useState<LegalModalType>(null);
  useEffect(() => {
    initPixel();
    trackPageView();
  }, []);
  const scrollToOffer = () => {
    document.getElementById('offer')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <div className="min-h-screen bg-offwhite font-sans text-warmgray overflow-x-hidden" data-id="element-54">
      {/* 1. HERO SECTION */}
      <section className="relative pt-20 pb-24 px-6 md:px-12 lg:px-24 max-w-7xl mx-auto flex flex-col lg:flex-row items-center gap-12" data-id="element-55">
        <div className="absolute top-10 left-10 hidden md:block" data-id="element-56">
          <LeafDecoration className="rotate-45" data-id="element-57" />
        </div>

        <div className="lg:w-1/2 z-10 flex flex-col items-start text-left" data-id="element-58">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-59">
            <span className="inline-block py-1 px-3 rounded-full bg-cream text-olive text-sm font-bold tracking-wider mb-6 border border-tan/30" data-id="element-60">
              NUEVO MÉTODO
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif text-olive leading-tight mb-4" data-id="element-61">
              Dejá de ser esclava de tu casa: El sistema de 5 minutos para
              recuperar tu paz mental
            </h1>
            <p className="text-xl md:text-2xl text-tan font-serif italic mb-6" data-id="element-62">
              (sin pasarte el fin de semana limpiando)
            </p>
            <h2 className="text-lg md:text-xl text-warmgray mb-10 leading-relaxed" data-id="element-63">
              Para la mujer que siente que el desorden la devora, que tiene
              "parálisis por análisis" y que está cansada de los métodos
              imposibles de las gurús del orden.
            </h2>

            <Button onClick={scrollToOffer} className="w-full md:w-auto" data-id="element-64">
              SÍ, QUIERO MI CASA EN CALMA
            </Button>
            <p className="text-sm text-warmgray mt-4 flex items-center gap-2" data-id="element-65">
              <ShieldCheck size={16} className="text-sage" data-id="element-66" /> Compra 100%
              segura. Acceso instantáneo.
            </p>
          </motion.div>
        </div>

        <motion.div className="lg:w-1/2 relative" initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.8,
        delay: 0.2
      }} data-id="element-67">
          <div className="absolute inset-0 bg-tan/10 rounded-full blur-3xl transform scale-110 -z-10" data-id="element-68"></div>
          <img src="https://cdn.magicpatterns.com/uploads/qYpXkRkCpWN95fmcSe5Dco/mockup_imagen_2.png" alt="Método Casa en Calma en laptop y tablet" className="w-full h-auto drop-shadow-2xl rounded-xl" data-id="element-69" />
        </motion.div>
      </section>

      {/* 2. OPENING HOOK & PROBLEM */}
      <section className="py-24 bg-cream relative" data-id="element-70">
        <div className="absolute right-10 top-20 hidden md:block" data-id="element-71">
          <LeafDecoration className="-rotate-12" data-id="element-72" />
        </div>
        <div className="max-w-3xl mx-auto px-6 text-center" data-id="element-73">
          <FadeIn data-id="element-74">
            <p className="text-xl md:text-2xl font-serif text-olive leading-relaxed mb-8" data-id="element-75">
              "Son las 11 de la noche de un domingo. Mirás a tu alrededor y
              sentís que el aire te falta. Una montaña de platos en la bacha,
              ropa sin doblar en el sillón y esa mesa del comedor que ya ni se
              ve de tantos papeles acumulados."
            </p>
            <p className="text-lg mb-12" data-id="element-76">
              No es que seas desordenada. Es que estás agotada. Tu casa, que
              debería ser tu refugio, se convirtió en tu segundo trabajo (y uno
              por el que nadie te paga).
            </p>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-77">
            <div className="bg-white p-8 md:p-12 rounded-3xl shadow-soft text-left relative overflow-hidden" data-id="element-78">
              <div className="absolute top-0 left-0 w-2 h-full bg-tan" data-id="element-79"></div>
              <h3 className="text-2xl md:text-3xl font-serif text-olive mb-6" data-id="element-80">
                El desorden visual no es solo "cosas tiradas". Es carga mental.
              </h3>
              <p className="mb-4" data-id="element-81">
                Cada objeto fuera de lugar es una tarea pendiente que te grita
                al oído mientras intentás descansar.
              </p>
              <ul className="space-y-4 mt-6" data-id="element-82">
                <li className="flex items-start gap-3" data-id="element-83">
                  <XCircle className="text-tan shrink-0 mt-1" size={20} data-id="element-84" />
                  <span data-id="element-85">Tu casa te roba energía.</span>
                </li>
                <li className="flex items-start gap-3" data-id="element-86">
                  <XCircle className="text-tan shrink-0 mt-1" size={20} data-id="element-87" />
                  <span data-id="element-88">
                    Te genera vergüenza cuando alguien quiere caer de sorpresa.
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-89">
                  <XCircle className="text-tan shrink-0 mt-1" size={20} data-id="element-90" />
                  <span data-id="element-91">
                    Te hace pelear con tu familia porque sentís que sos la única
                    que ve el caos.
                  </span>
                </li>
              </ul>
              <p className="mt-6 font-bold text-olive" data-id="element-92">
                Lo peor de todo: sentís que para solucionarlo necesitás 3 días
                libres que no tenés.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 3. WHY IT FAILED */}
      <section className="py-24 px-6 max-w-4xl mx-auto" data-id="element-93">
        <FadeIn data-id="element-94">
          <div className="text-center mb-16" data-id="element-95">
            <h2 className="text-3xl md:text-4xl font-serif text-olive mb-6" data-id="element-96">
              Por qué lo que intentaste no funcionó
            </h2>
            <p className="text-lg" data-id="element-97">
              Si sentís que probaste de todo y nada dura, no es tu culpa.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8" data-id="element-98">
            {[{
            title: 'El mito de las 8 horas',
            desc: 'Los métodos tradicionales tipo "Marie Kondo" te piden vaciar todo el placard, pero vos no tenés 8 horas libres para decidir qué remera te da "alegría".'
          }, {
            title: 'La trampa estética',
            desc: 'Comprar canastos lindos no sirve si no tenés un sistema para que las cosas entren y salgan de ellos. Terminan siendo contenedores de caos.'
          }, {
            title: 'El ciclo del sábado',
            desc: 'Limpiar a fondo el sábado es una trampa: te agota el fin de semana y para el lunes a la tarde, el caos ya volvió a dominar tu casa.'
          }].map((item, i) => <div key={i} className="bg-white p-6 rounded-2xl shadow-sm border border-cream-dark" data-id="element-99">
                <h4 className="font-serif text-xl text-olive mb-3" data-id="element-100">
                  {item.title}
                </h4>
                <p className="text-sm" data-id="element-101">{item.desc}</p>
              </div>)}
          </div>
        </FadeIn>
      </section>

      {/* 4. UNIQUE MECHANISM */}
      <section className="py-24 bg-olive text-offwhite relative overflow-hidden" data-id="element-102">
        <div className="absolute -right-20 -top-20 opacity-10" data-id="element-103">
          <Leaf size={300} data-id="element-104" />
        </div>
        <div className="max-w-4xl mx-auto px-6 relative z-10 text-center" data-id="element-105">
          <FadeIn data-id="element-106">
            <h2 className="text-3xl md:text-5xl font-serif text-cream mb-8" data-id="element-107">
              Te presento el Método Casa en Calma
            </h2>
            <p className="text-xl md:text-2xl font-serif italic text-tan-light mb-8" data-id="element-108">
              Este no es un manual de limpieza. Es un sistema de
              Micro-Decisiones de 5 Minutos.
            </p>
            <p className="text-lg leading-relaxed mb-10 text-cream/90" data-id="element-109">
              Aprendí que el orden no es una cuestión de tiempo, es una cuestión
              de límites operativos. Vamos a hackear tu cerebro para que el
              orden ocurra en los "huecos" de tu día, sin esfuerzo y sin
              sacrificio.
            </p>
            <Button onClick={scrollToOffer} className="bg-tan hover:bg-tan-light text-olive mx-auto" data-id="element-110">
              QUIERO CONOCER EL MÉTODO
            </Button>
          </FadeIn>
        </div>
      </section>

      {/* 5. DELIVERABLES */}
      <section className="py-24 px-6 bg-offwhite" data-id="element-111">
        <div className="max-w-5xl mx-auto" data-id="element-112">
          <FadeIn data-id="element-113">
            <div className="text-center mb-16" data-id="element-114">
              <h2 className="text-3xl md:text-4xl font-serif text-olive mb-4" data-id="element-115">
                ¿Qué vas a lograr?
              </h2>
              <p className="text-lg" data-id="element-116">
                Todo lo que incluye tu acceso inmediato hoy:
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6" data-id="element-117">
              {[{
              icon: <BookOpen className="text-sage" size={32} data-id="element-118" />,
              title: 'Ebook Método Casa en Calma',
              desc: 'La guía paso a paso para implementar el sistema de micro-decisiones en tu vida diaria.'
            }, {
              icon: <Clock className="text-sage" size={32} data-id="element-119" />,
              title: 'La Regla de los 300 Segundos',
              desc: 'El truco mental definitivo para eliminar la procrastinación al instante.'
            }, {
              icon: <ShieldCheck className="text-sage" size={32} data-id="element-120" />,
              title: 'El Protocolo de Rescate (15 min)',
              desc: 'Qué hacer exactamente cuando te avisan que una visita está por llegar y tu casa es un caos.'
            }, {
              icon: <ListChecks className="text-sage" size={32} data-id="element-121" />,
              title: 'Planilla de Ritual de Cierre',
              desc: 'El checklist nocturno de 10 minutos para que el lunes a la mañana no sea un infierno.'
            }, {
              icon: <Map className="text-sage" size={32} data-id="element-122" />,
              title: 'Mapa de Puntos Calientes',
              desc: 'Cómo identificar y neutralizar los imanes de desorden específicos en tu hogar.'
            }].map((item, i) => <div key={i} className="bg-white p-8 rounded-2xl shadow-soft flex gap-6 items-start hover:shadow-md transition-shadow" data-id="element-123">
                  <div className="bg-cream p-3 rounded-xl shrink-0" data-id="element-124">
                    {item.icon}
                  </div>
                  <div data-id="element-125">
                    <h4 className="font-serif text-xl text-olive mb-2" data-id="element-126">
                      {item.title}
                    </h4>
                    <p className="text-warmgray" data-id="element-127">{item.desc}</p>
                  </div>
                </div>)}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 6. AUDIENCE (FOR / NOT FOR) */}
      <section className="py-24 bg-cream px-6" data-id="element-128">
        <div className="max-w-5xl mx-auto" data-id="element-129">
          <FadeIn data-id="element-130">
            <div className="grid md:grid-cols-2 gap-12" data-id="element-131">
              {/* For Who */}
              <div className="bg-white p-8 md:p-10 rounded-3xl shadow-soft" data-id="element-132">
                <h3 className="text-2xl font-serif text-olive mb-8 flex items-center gap-3" data-id="element-133">
                  <CheckCircle2 className="text-sage" size={28} data-id="element-134" />
                  Para quién ES esto
                </h3>
                <ul className="space-y-6" data-id="element-135">
                  <li className="flex gap-4" data-id="element-136">
                    <div className="w-2 h-2 rounded-full bg-sage mt-2 shrink-0" data-id="element-137"></div>
                    <p data-id="element-138">
                      Mujeres que trabajan dentro o fuera de casa y sienten que
                      el desorden les quita calidad de vida.
                    </p>
                  </li>
                  <li className="flex gap-4" data-id="element-139">
                    <div className="w-2 h-2 rounded-full bg-sage mt-2 shrink-0" data-id="element-140"></div>
                    <p data-id="element-141">
                      Madres que luchan contra la acumulación interminable de
                      juguetes, papeles y ropa.
                    </p>
                  </li>
                  <li className="flex gap-4" data-id="element-142">
                    <div className="w-2 h-2 rounded-full bg-sage mt-2 shrink-0" data-id="element-143"></div>
                    <p data-id="element-144">
                      Personas que se sienten abrumadas y no saben por dónde
                      empezar a organizar.
                    </p>
                  </li>
                </ul>
              </div>

              {/* Not For Who */}
              <div className="bg-offwhite border border-cream-dark p-8 md:p-10 rounded-3xl" data-id="element-145">
                <h3 className="text-2xl font-serif text-warmgray mb-8 flex items-center gap-3" data-id="element-146">
                  <XCircle className="text-red-400" size={28} data-id="element-147" />
                  Para quién NO es
                </h3>
                <ul className="space-y-6" data-id="element-148">
                  <li className="flex gap-4" data-id="element-149">
                    <div className="w-2 h-2 rounded-full bg-red-300 mt-2 shrink-0" data-id="element-150"></div>
                    <p data-id="element-151">
                      Personas que buscan consejos de limpieza profunda o
                      desinfección (esto es gestión de orden, no limpieza).
                    </p>
                  </li>
                  <li className="flex gap-4" data-id="element-152">
                    <div className="w-2 h-2 rounded-full bg-red-300 mt-2 shrink-0" data-id="element-153"></div>
                    <p data-id="element-154">
                      Quienes esperan que el orden se mantenga solo por arte de
                      magia sin dedicarle siquiera 5 minutos al día.
                    </p>
                  </li>
                </ul>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 7. HOW IT WORKS */}
      <section className="py-24 px-6 max-w-4xl mx-auto" data-id="element-155">
        <FadeIn data-id="element-156">
          <div className="text-center mb-16" data-id="element-157">
            <h2 className="text-3xl md:text-4xl font-serif text-olive mb-4" data-id="element-158">
              ¿Cómo funciona?
            </h2>
            <p className="text-lg" data-id="element-159">
              Un proceso simple y directo para empezar hoy.
            </p>
          </div>

          <div className="relative" data-id="element-160">
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-cream-dark hidden md:block" data-id="element-161"></div>
            <div className="space-y-12" data-id="element-162">
              {[{
              step: '1',
              title: 'Descarga',
              desc: 'Recibís el acceso inmediato a todo el material en tu email tras el pago seguro.'
            }, {
              step: '2',
              title: 'Identifica',
              desc: 'Aplicás el escaneo de "Puntos Calientes" para saber exactamente dónde está el fuego en tu casa.'
            }, {
              step: '3',
              title: 'Ejecuta',
              desc: 'Empezás con el Ritual de Cierre de 10 minutos hoy mismo antes de dormir.'
            }].map((item, i) => <div key={i} className="relative flex flex-col md:flex-row gap-6 md:gap-12 items-start md:items-center" data-id="element-163">
                  <div className="w-16 h-16 rounded-full bg-sage text-white flex items-center justify-center text-2xl font-serif font-bold shrink-0 z-10 shadow-md" data-id="element-164">
                    {item.step}
                  </div>
                  <div className="bg-white p-6 md:p-8 rounded-2xl shadow-sm border border-cream flex-1" data-id="element-165">
                    <h4 className="text-xl font-serif text-olive mb-2" data-id="element-166">
                      {item.title}
                    </h4>
                    <p data-id="element-167">{item.desc}</p>
                  </div>
                </div>)}
            </div>
          </div>
        </FadeIn>
      </section>

      {/* 8. CREDIBILITY / IMAGINE */}
      <section className="py-24 bg-tan/10 px-6 relative" data-id="element-168">
        <div className="absolute left-10 bottom-10 hidden md:block" data-id="element-169">
          <LeafDecoration className="-rotate-45" data-id="element-170" />
        </div>
        <div className="max-w-3xl mx-auto text-center" data-id="element-171">
          <FadeIn data-id="element-172">
            <h2 className="text-3xl md:text-4xl font-serif text-olive mb-8 leading-tight" data-id="element-173">
              Imaginate despertar mañana, caminar hacia la cocina y encontrar la
              mesada vacía.
            </h2>
            <p className="text-xl mb-6" data-id="element-174">
              Imaginate poder abrir la puerta de tu casa a una amiga un martes a
              la tarde sin sentir ese nudo en el estómago por el desorden.
            </p>
            <p className="text-lg font-bold text-olive" data-id="element-175">
              Este método no te da una casa de revista; te da una casa donde
              podés respirar.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* 9. FAQ */}
      <section className="py-24 px-6 max-w-3xl mx-auto" data-id="element-176">
        <FadeIn data-id="element-177">
          <div className="text-center mb-16" data-id="element-178">
            <h2 className="text-3xl md:text-4xl font-serif text-olive mb-4" data-id="element-179">
              Preguntas Frecuentes
            </h2>
          </div>

          <div className="space-y-6" data-id="element-180">
            {[{
            q: '¿Es un libro físico?',
            a: 'No, es un producto digital de descarga inmediata. Lo recibís en tu email al instante para que puedas empezar a aplicar el método hoy mismo.'
          }, {
            q: '¿Sirve para casas chicas?',
            a: 'Especialmente para casas chicas. En los espacios reducidos, el desorden se nota el doble y afecta más rápido. El método de micro-decisiones es ideal para mantener el flujo en espacios pequeños.'
          }, {
            q: '¿Cómo lo pago?',
            a: 'Aceptamos todos los medios de pago (Mercado Pago, tarjetas de crédito, tarjetas de débito). El pago es 100% seguro.'
          }].map((faq, i) => <div key={i} className="bg-white p-6 rounded-2xl shadow-sm border border-cream" data-id="element-181">
                <h4 className="text-lg font-bold text-olive mb-3 flex items-center gap-2" data-id="element-182">
                  <ChevronDown className="text-tan" size={20} data-id="element-183" />
                  {faq.q}
                </h4>
                <p className="pl-7 text-warmgray" data-id="element-184">{faq.a}</p>
              </div>)}
          </div>
        </FadeIn>
      </section>

      {/* 10. OFFER & CTA */}
      <section id="offer" className="py-24 bg-olive text-center px-6 relative overflow-hidden" data-id="element-185">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(196,168,130,0.15)_0%,transparent_70%)]" data-id="element-186"></div>
        <div className="max-w-2xl mx-auto relative z-10" data-id="element-187">
          <FadeIn data-id="element-188">
            <div className="bg-white p-8 md:p-12 rounded-3xl shadow-2xl" data-id="element-189">
              <h2 className="text-3xl md:text-4xl font-serif text-olive mb-6" data-id="element-190">
                Recuperá tu hogar hoy
              </h2>

              <div className="my-8 py-8 border-y border-cream" data-id="element-191">
                <div className="flex flex-col items-center gap-1" data-id="element-192">
                  <span className="inline-block bg-tan/20 text-tan font-bold text-sm px-3 py-1 rounded-full tracking-wide" data-id="element-193">
                    60% OFF
                  </span>
                  <div className="text-xl text-warmgray/60 line-through font-sans" data-id="element-194">
                    $22.475 ARS
                  </div>
                  <div className="text-5xl md:text-6xl font-serif text-sage font-bold" data-id="element-195">
                    $8.990{' '}
                    <span className="text-2xl text-warmgray font-sans font-normal" data-id="element-196">
                      ARS
                    </span>
                  </div>
                </div>
                <ul className="flex flex-col items-center gap-2 mt-6 text-warmgray" data-id="element-197">
                  <li className="flex items-center gap-2" data-id="element-198">
                    <CheckCircle2 size={18} className="text-sage" data-id="element-199" /> Un pago
                    único
                  </li>
                  <li className="flex items-center gap-2" data-id="element-200">
                    <CheckCircle2 size={18} className="text-sage" data-id="element-201" /> Acceso de
                    por vida
                  </li>
                  <li className="flex items-center gap-2" data-id="element-202">
                    <CheckCircle2 size={18} className="text-sage" data-id="element-203" /> Paz mental
                    inmediata
                  </li>
                </ul>
              </div>

              <Button onClick={() => {
              trackAddToCart();
              window.open('https://vidaenequilibrio.online/cart/55625187098697:1', '_blank');
            }} className="w-full text-xl py-5 mb-4" data-id="element-204">
                👉 SÍ, QUIERO MI CASA EN CALMA
              </Button>

              <div className="flex items-center justify-center gap-4 text-sm text-warmgray mt-6" data-id="element-205">
                <span className="flex items-center gap-1" data-id="element-206">
                  <ShieldCheck size={16} data-id="element-207" /> Compra 100% segura
                </span>
                <span className="hidden md:inline" data-id="element-208">•</span>
                <span data-id="element-209">Acceso instantáneo por email</span>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-12 bg-offwhite text-center border-t border-cream" data-id="element-210">
        <div className="flex justify-center mb-6" data-id="element-211">
          <Leaf className="text-tan" size={32} data-id="element-212" />
        </div>
        <div className="flex items-center justify-center gap-4 mb-4" data-id="element-213">
          <button onClick={() => setLegalModal('privacy')} className="text-sm text-warmgray hover:text-sage transition-colors underline underline-offset-2" data-id="element-214">
            Política de Privacidad
          </button>
          <span className="text-warmgray/40" data-id="element-215">|</span>
          <button onClick={() => setLegalModal('terms')} className="text-sm text-warmgray hover:text-sage transition-colors underline underline-offset-2" data-id="element-216">
            Términos y Condiciones
          </button>
        </div>
        <p className="text-sm text-warmgray mb-2" data-id="element-217">
          © {new Date().getFullYear()} Método Casa en Calma. Todos los derechos
          reservados.
        </p>
        <p className="text-xs text-warmgray/60 max-w-md mx-auto px-6" data-id="element-218">
          Este sitio no es parte del sitio web de Facebook o Facebook Inc.
          Además, este sitio NO está respaldado por Facebook de ninguna manera.
          FACEBOOK es una marca registrada de FACEBOOK, Inc.
        </p>
      </footer>

      <LegalModal type={legalModal} onClose={() => setLegalModal(null)} data-id="element-219" />
    </div>;
}